import { Component, OnInit, OnDestroy, Inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatRadioModule } from '@angular/material/radio';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatAngularModule } from '../../shared/modules/mat-angular.module';
import { PromptResearchService, PromptResearchCreateRequest, PromptResearch, GeneratedPrompt, PromptResearchCreateTaskStatus } from '../../services/prompt-research.service';
import { CountryService } from '../../services/country.service';
import { LanguageService, LanguageOption } from '../../services/language.service';
import { TaskProgressWebSocketService } from '../../services/task-progress-websocket.service';
import { Subject, timer, switchMap, takeUntil, filter, take, Subscription } from 'rxjs';

export interface PromptResearchDialogData {
  projectId?: string | null;
  researchId?: string;
  research?: PromptResearch;
  mode: 'create' | 'view';
  primary_country?: string; // Project's primary country to use as default
  language?: string; // Project's language so prompts/data come in this language (e.g. "English")
}

@Component({
  selector: 'app-prompt-research-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAngularModule
  ],
  templateUrl: './prompt-research-dialog.component.html',
  styleUrl: './prompt-research-dialog.component.scss'
})
export class PromptResearchDialogComponent implements OnInit, OnDestroy {
  currentStep: number = 1; // 1 = Form, 2 = Results
  loading = false;
  generating = false;
  private destroy$ = new Subject<void>();
  private createTaskWsSub: Subscription | null = null;

  // Form data
  researchType: 'seo_keywords' | 'url_analysis' | 'brand_brainstorm' = 'seo_keywords';
  keywords: string = '';
  url: string = '';
  brandInfo = {
    brand_name: '',
    industry: '',
    products: '',
    target_audience: ''
  };
  language: string = 'English'; // default English; we send label to API so backend needs no language map
  country: string = 'Global';

  // Results
  prompts: GeneratedPrompt[] = [];
  researchResult: PromptResearch | null = null;

  // Country and language options
  countryOptions: string[] = [];
  languageOptions: LanguageOption[] = [];

  constructor(
    public dialogRef: MatDialogRef<PromptResearchDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PromptResearchDialogData,
    private promptResearchService: PromptResearchService,
    private countryService: CountryService,
    private languageService: LanguageService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private taskProgressWs: TaskProgressWebSocketService
  ) {}

  ngOnInit(): void {
    // When creating, use project language so prompts/data come in that language
    if (this.data.mode === 'create' && this.data.language) {
      this.language = this.data.language;
    }
    // Check mode FIRST and set currentStep immediately to prevent showing wrong step
    if (this.data.mode === 'view') {
      // If viewing existing research, set step to 2 immediately
      this.currentStep = 2;
      
      // If research data is provided directly, use it
      if (this.data.research) {
        this.researchResult = this.data.research;
        this.prompts = this.normalizePrompts(this.data.research.prompts || []);
        // Set country from research data if available
        if (this.data.research.country) {
          this.country = this.data.research.country;
        }
        if (this.data.research.language) {
          this.language = this.languageService.getLanguageLabel(this.data.research.language) || this.data.research.language;
        }
      } else if (this.data.researchId) {
        // If only researchId is provided, load it
        this.loadResearch();
      }
    } else {
      // Create mode - ensure we're on step 1
      this.currentStep = 1;
      // Auto-select project's primary country if available
      if (this.data.primary_country) {
        this.country = this.data.primary_country;
      }
    }
    
    // Load country and language options (async, doesn't affect initial step)
    this.countryService.getCountryOptions().subscribe(options => {
      this.countryOptions = options;
      this.cdr.detectChanges();
    });
    this.languageService.getLanguageOptions().subscribe(options => {
      this.languageOptions = options;
      this.cdr.detectChanges();
    });
  }

  loadResearch(): void {
    if (!this.data.researchId) return;
    
    this.loading = true;
    this.promptResearchService.getResearchById(this.data.researchId).subscribe({
      next: (research) => {
        this.researchResult = research;
        this.prompts = this.normalizePrompts(research.prompts || []);
        // Set country from research data if available
        if (research.country) {
          this.country = research.country;
        }
        if (research.language) {
          this.language = this.languageService.getLanguageLabel(research.language) || research.language;
        }
        this.currentStep = 2;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading research:', err);
        this.snackBar.open('Failed to load research', 'Close', { duration: 3000 });
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  onNextStep(): void {
    if (this.currentStep === 1) {
      // Validate form based on research type
      if (!this.validateForm()) {
        return;
      }

      // Generate prompts
      this.generatePrompts();
    }
  }

  validateForm(): boolean {
    if (this.researchType === 'seo_keywords') {
      if (!this.keywords.trim()) {
        this.snackBar.open('Please enter SEO keywords', 'Close', { duration: 3000 });
        return false;
      }
      const keywordList = this.keywords.split('\n').filter(k => k.trim());
      if (keywordList.length === 0) {
        this.snackBar.open('Please enter at least one keyword', 'Close', { duration: 3000 });
        return false;
      }
      if (keywordList.length > 20) {
        this.snackBar.open('Maximum 20 keywords allowed', 'Close', { duration: 3000 });
        return false;
      }
    } else if (this.researchType === 'url_analysis') {
      if (!this.url.trim()) {
        this.snackBar.open('Please enter a URL', 'Close', { duration: 3000 });
        return false;
      }
      try {
        new URL(this.url);
      } catch {
        this.snackBar.open('Please enter a valid URL', 'Close', { duration: 3000 });
        return false;
      }
    } else if (this.researchType === 'brand_brainstorm') {
      if (!this.brandInfo.brand_name.trim()) {
        this.snackBar.open('Please enter brand name', 'Close', { duration: 3000 });
        return false;
      }
    }
    return true;
  }

  /** Handle completed or failed status (from WebSocket or polling). */
  private onCreateTaskStatus(status: PromptResearchCreateTaskStatus): void {
    if (status.status === 'completed' && status.research_id) {
      this.promptResearchService.getResearchById(status.research_id).subscribe({
        next: (research) => {
          this.researchResult = research;
          this.prompts = this.normalizePrompts(research.prompts || []);
          this.currentStep = 2;
          this.generating = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.generating = false;
          this.snackBar.open('Prompts created but failed to load. Refresh the list.', 'Close', { duration: 4000 });
          this.cdr.detectChanges();
        }
      });
    } else if (status.status === 'failed') {
      this.generating = false;
      this.snackBar.open(status.error_message || 'Prompt research failed.', 'Close', { duration: 5000 });
      this.cdr.detectChanges();
    }
  }

  /** Stop WebSocket subscription for create task (same pattern as bulk search). */
  private stopCreateTaskProgress(): void {
    if (this.createTaskWsSub) {
      this.createTaskWsSub.unsubscribe();
      this.createTaskWsSub = null;
    }
  }

  generatePrompts(): void {
    this.generating = true;

    const request: PromptResearchCreateRequest = {
      research_type: this.researchType,
      input_data: this.getInputData(),
      language: this.language,
      country: this.country,
      project_id: this.data.projectId || undefined
    };

    this.promptResearchService.createResearch(request).subscribe({
      next: (res) => {
        const taskId = res.task_id;

        if (this.taskProgressWs.useWebSocket) {
          this.stopCreateTaskProgress();
          this.taskProgressWs.connect();
          this.createTaskWsSub = this.taskProgressWs
            .messagesByPath('prompt_research_create')
            .pipe(
              filter((msg) => (msg.data as { task_id?: string })?.task_id === taskId),
              takeUntil(this.destroy$)
            )
            .subscribe((msg) => {
              const data = msg.data as unknown as PromptResearchCreateTaskStatus;
              if (!data || data.status !== 'completed' && data.status !== 'failed') return;
              this.stopCreateTaskProgress();
              this.onCreateTaskStatus(data);
            });
          this.promptResearchService.getCreateStatus(taskId).subscribe({
            next: (status) => {
              if (status.status === 'completed' || status.status === 'failed') {
                this.stopCreateTaskProgress();
                this.onCreateTaskStatus(status);
              }
            },
            error: () => {
              this.generating = false;
              this.snackBar.open('Failed to get task status.', 'Close', { duration: 4000 });
              this.cdr.detectChanges();
            }
          });
          return;
        }

        timer(0, 2000).pipe(
          switchMap(() => this.promptResearchService.getCreateStatus(taskId)),
          filter(s => s.status === 'completed' || s.status === 'failed'),
          take(1),
          takeUntil(this.destroy$)
        ).subscribe({
          next: (status) => this.onCreateTaskStatus(status),
          error: () => {
            this.generating = false;
            this.snackBar.open('Failed to get task status.', 'Close', { duration: 4000 });
            this.cdr.detectChanges();
          }
        });
      },
      error: (err) => {
        console.error('Error starting prompt research:', err);
        this.snackBar.open(
          err.error?.detail || 'Failed to start prompt research. Please try again.',
          'Close',
          { duration: 5000 }
        );
        this.generating = false;
        this.cdr.detectChanges();
      }
    });
  }

  ngOnDestroy(): void {
    this.stopCreateTaskProgress();
    this.destroy$.next();
    this.destroy$.complete();
  }

  getInputData(): any {
    if (this.researchType === 'seo_keywords') {
      return {
        keywords: this.keywords.split('\n').filter(k => k.trim())
      };
    } else if (this.researchType === 'url_analysis') {
      return {
        url: this.url
      };
    } else {
      return {
        brand_info: {
          brand_name: this.brandInfo.brand_name,
          industry: this.brandInfo.industry,
          products: this.brandInfo.products.split(',').filter(p => p.trim()),
          target_audience: this.brandInfo.target_audience
        }
      };
    }
  }

  copyAllPrompts(): void {
    const text = this.prompts.map(p => p.text).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      this.snackBar.open('Prompts copied to clipboard', 'Close', { duration: 2000 });
    }).catch(() => {
      this.snackBar.open('Failed to copy prompts', 'Close', { duration: 3000 });
    });
  }

  getVolumeGradient(volume: number): string {
    // Create gradient based on volume (0-100000)
    // Higher volume = more purple/pink
    const normalized = Math.min(volume / 100000, 1);
    const hue = 280 - (normalized * 30); // Purple (280) to Pink (250)
    const saturation = 60 + (normalized * 30); // 60% to 90%
    const lightness = 75 - (normalized * 15); // 75% to 60%
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  }

  isHighRelevanceScore(score: number): boolean {
    return score >= 8.0;
  }

  exportToCSV(): void {
    if (!this.researchResult) return;
    
    this.promptResearchService.exportResearch(this.researchResult.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `prompt_research_${this.researchResult!.id}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.snackBar.open('Research exported successfully', 'Close', { duration: 2000 });
      },
      error: (err) => {
        console.error('Error exporting research:', err);
        this.snackBar.open('Failed to export research', 'Close', { duration: 3000 });
      }
    });
  }

  onClose(): void {
    if (this.currentStep === 2 && this.researchResult) {
      this.dialogRef.close('success');
    } else {
      this.dialogRef.close();
    }
  }

  getKeywordCount(): number {
    return this.keywords.split('\n').filter(k => k.trim()).length;
  }

  normalizePrompts(prompts: any[]): GeneratedPrompt[] {
    // Handle both new format (GeneratedPrompt[]) and legacy format (string[])
    return prompts.map(p => {
      if (typeof p === 'string') {
        // Legacy format: string
        return {
          text: p,
          relevance_score: 5.0,
          monthly_intent_volume: 100
        };
      } else {
        // New format: GeneratedPrompt object
        return {
          text: p.text || '',
          relevance_score: p.relevance_score ?? 5.0,
          monthly_intent_volume: p.monthly_intent_volume ?? 100
        };
      }
    });
  }
}
